import { signal } from '@preact/signals-core';
import {
  activeRoleSignal,
  getAuthHeaders,
  refreshAssets
  , searchCustomersForAutocomplete
} from '../assets';
import { escapeHtml } from '../../utils/sanitize';
import { showToast } from '../../utils/toast';

// ─── Signals / Local state ─────────────────────────────────────────────────────
export const mergeSubmittingSignal = signal<boolean>(false);

let selectedSourceCustomerId: string | null = null;
let selectedTargetCustomerId: string | null = null;

// ─── Rendering ─────────────────────────────────────────────────────────────────
export function renderCustomerMergePanel(): string {
  const role = activeRoleSignal.value;
  
  // Route Guard check inside render for safety
  if (role !== 'superadmin' && role !== 'admin') {
    return `
      <s-page heading="Unauthorized">
        <s-banner tone="critical">
          Access Denied: You do not have administrative privileges to access this panel.
        </s-banner>
      </s-page>
    `;
  }

  return `
    <div style="padding: 2rem; max-width: 600px; margin: 0 auto;">
      <s-box padding="base" background="surface" border="base" borderRadius="base">
        <s-stack gap="base">
          <s-heading element="h1" style="font-size: 1.5rem; font-weight: 700; margin: 0;">Customer Merge Utility</s-heading>
          
          <s-banner tone="info">
            Relocate all assets, sites, tickets, contracts, and internal memos from a source customer profile into a target profile. The source profile will be soft-deleted.
          </s-banner>

          <!-- Source Customer Autocomplete -->
          <div style="position:relative; z-index:100;">
            <opm-autocomplete
              id="merge-source-customer"
              label="Source Customer (Profile to absorb)"
              placeholder="Search for source customer…"
            ></opm-autocomplete>
          </div>

          <!-- Target Customer Autocomplete -->
          <div style="position:relative; z-index:90;">
            <opm-autocomplete
              id="merge-target-customer"
              label="Target Customer (Profile that absorbs)"
              placeholder="Search for target customer…"
            ></opm-autocomplete>
          </div>

          <div id="merge-validation-error" style="color: var(--p-color-text-critical, #d72c0d); font-size: 0.875rem; display: none;"></div>

          <s-stack direction="row" gap="base" align="center" style="margin-top: 1rem;">
            <s-button id="btn-trigger-merge" variant="primary">Execute Merge</s-button>
          </s-stack>
        </s-stack>
      </s-box>

      <!-- Confirmation Modal -->
      <s-modal id="merge-confirmation-modal" heading="Confirm Customer Merge">
        <s-stack gap="base" style="padding: 1rem;">
          <s-banner tone="critical">
            Warning: This action will relocate all assets, contracts, tickets, service sessions, sites, and invoices from 
            '<span id="confirm-source-name" style="font-weight:700;"></span>' to 
            '<span id="confirm-target-name" style="font-weight:700;"></span>' and soft-delete 
            '<span id="confirm-source-name-2" style="font-weight:700;"></span>'. This is irreversible.
          </s-banner>
          
          <s-text>To confirm, type the word <strong style="color:var(--p-color-text-critical, #d72c0d);">MERGE</strong> in the box below:</s-text>
          
          <s-text-field 
            id="merge-confirmation-input" 
            placeholder="Type MERGE to confirm" 
            label="Confirmation Code"
            name="confirmCode"
          ></s-text-field>

          <s-stack direction="row" gap="base" align="center" style="margin-top: 1rem; justify-content: flex-end;">
            <s-button id="btn-cancel-merge" commandFor="merge-confirmation-modal" command="--hide">Cancel</s-button>
            <s-button id="btn-submit-merge" variant="primary" disabled>Execute Merge</s-button>
          </s-stack>
        </s-stack>
      </s-modal>
    </div>
  `;
}

// ─── Listeners Binding ──────────────────────────────────────────────────────────
export function setupCustomerMergePanelListeners() {
  const role = activeRoleSignal.value;
  if (role !== 'superadmin' && role !== 'admin') return;

  const autocompleteSource = document.getElementById('merge-source-customer') as any;
  const autocompleteTarget = document.getElementById('merge-target-customer') as any;
  const validationErrorDiv = document.getElementById('merge-validation-error');
  const triggerMergeBtn = document.getElementById('btn-trigger-merge') as HTMLButtonElement;

  selectedSourceCustomerId = null;
  selectedTargetCustomerId = null;

  // Initialize Autocompletes
  if (autocompleteSource) {
    autocompleteSource.loadItems = searchCustomersForAutocomplete;
    autocompleteSource.addEventListener('select', (e: CustomEvent) => {
      selectedSourceCustomerId = e.detail.id;
      validateInputs();
    });
  }

  if (autocompleteTarget) {
    autocompleteTarget.loadItems = searchCustomersForAutocomplete;
    autocompleteTarget.addEventListener('select', (e: CustomEvent) => {
      selectedTargetCustomerId = e.detail.id;
      validateInputs();
    });
  }

  const showValidationError = (msg: string) => {
    if (validationErrorDiv) {
      validationErrorDiv.innerHTML = `
        <s-banner tone="critical" title="Validation Error">
          <s-paragraph>${escapeHtml(msg)}</s-paragraph>
        </s-banner>
      `;
      validationErrorDiv.style.display = 'block';
    }
  };

  const clearValidationError = () => {
    if (validationErrorDiv) {
      validationErrorDiv.style.display = 'none';
      validationErrorDiv.innerHTML = '';
    }
  };

  function validateInputs(): boolean {
    if (!validationErrorDiv) return false;

    if (!selectedSourceCustomerId || !selectedTargetCustomerId) {
      clearValidationError();
      return false;
    }

    if (selectedSourceCustomerId === selectedTargetCustomerId) {
      showValidationError('Source and target customers must be different.');
      return false;
    }

    clearValidationError();
    return true;
  }

  // Trigger Confirmation Modal
  if (triggerMergeBtn) {
    triggerMergeBtn.addEventListener('click', () => {
      if (!selectedSourceCustomerId || !selectedTargetCustomerId) {
        showToast('Please select both a source and target customer.', 'error');
        return;
      }
      if (!validateInputs()) return;

      const sourceName = (autocompleteSource?.query || 'Source').trim() || 'Source';
      const targetName = (autocompleteTarget?.query || 'Target').trim() || 'Target';

      const s1 = document.getElementById('confirm-source-name');
      const s2 = document.getElementById('confirm-source-name-2');
      const t1 = document.getElementById('confirm-target-name');
      
      if (s1) s1.textContent = sourceName;
      if (s2) s2.textContent = sourceName;
      if (t1) t1.textContent = targetName;

      // Reset confirm input
      const confirmInput = document.getElementById('merge-confirmation-input') as HTMLInputElement;
      if (confirmInput) {
        confirmInput.value = '';
        confirmInput.dispatchEvent(new Event('input')); // Reset disabled state
      }

      const modal = document.getElementById('merge-confirmation-modal') as any;
      if (modal && typeof modal.showOverlay === 'function') {
        modal.showOverlay();
      }
    });
  }

  // Handle confirmation MERGE text input validation
  const confirmInput = document.getElementById('merge-confirmation-input') as any;
  const submitMergeBtn = document.getElementById('btn-submit-merge') as HTMLButtonElement;

  if (confirmInput && submitMergeBtn) {
    confirmInput.addEventListener('input', () => {
      const val = (confirmInput.value || '').trim();
      submitMergeBtn.disabled = val !== 'MERGE';
    });
  }

  // Submit Merge Action
  if (submitMergeBtn) {
    submitMergeBtn.addEventListener('click', async () => {
      if (confirmInput.value !== 'MERGE') return;
      if (!selectedSourceCustomerId || !selectedTargetCustomerId) return;

      submitMergeBtn.disabled = true;
      submitMergeBtn.textContent = 'Merging…';

      try {
        const res = await fetch('/api/v1/tickets/merge-customers', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            sourceCustomerId: selectedSourceCustomerId,
            targetCustomerId: selectedTargetCustomerId
          })
        });

        if (res.ok) {
          const modal = document.getElementById('merge-confirmation-modal') as any;
          if (modal && typeof modal.hideOverlay === 'function') {
            modal.hideOverlay();
          }
          
          showToast('Customers merged successfully.', 'success');
          
          // Refresh lists and redirect
          await refreshAssets();
          
          setTimeout(() => {
            window.location.hash = '#customers';
          }, 1500);
        } else {
          const errBody = await res.json().catch(() => ({ error: 'Merge execution failed' }));
          showToast(errBody.error || 'Failed to merge customers.', 'error');
          submitMergeBtn.disabled = false;
          submitMergeBtn.textContent = 'Execute Merge';
        }
      } catch (e) {
        console.error('Customer merge failed:', e);
        showToast('Network error while executing merge.', 'error');
        submitMergeBtn.disabled = false;
        submitMergeBtn.textContent = 'Execute Merge';
      }
    });
  }
}
